import { useEffect, useState } from "react";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { ChevronLeft, ChevronRight, X, Download, Star } from "lucide-react";
import { getPhotoTag } from "./getPhotoTag";
import type { PhotoWithUrl } from "./types";

interface PhotoLightboxProps {
  photosWithUrls: PhotoWithUrl[];
  selectedIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDownload: (photo: PhotoWithUrl) => void;
  onTagPhoto: (photoId: string, tag: string) => void;
}

export function PhotoLightbox({
  photosWithUrls,
  selectedIndex,
  onClose,
  onPrev,
  onNext,
  onDownload,
  onTagPhoto,
}: PhotoLightboxProps) {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const selectedPhoto = photosWithUrls[selectedIndex];

  // Reset zoom/pan when switching photos
  useEffect(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, [selectedIndex]);

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.5, 5));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.5, 0.5));
  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsPanning(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  if (!selectedPhoto) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/98 backdrop-blur-xl flex items-center justify-center animate-fade-in"
      onClick={onClose}
      onWheel={handleWheel}
    >
      {/* Header with photo info and close */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent backdrop-blur-sm px-6 py-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-semibold text-lg">{selectedPhoto.document_name}</h3>
            {getPhotoTag(selectedPhoto) && (
              <Badge className="bg-primary text-primary-foreground">
                {getPhotoTag(selectedPhoto)}
              </Badge>
            )}
            <span className="text-white/70 text-sm">
              {selectedIndex + 1} / {photosWithUrls.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Left navigation arrow */}
      {selectedIndex > 0 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 backdrop-blur-sm h-12 w-12 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>
      )}

      {/* Right navigation arrow */}
      {selectedIndex < photosWithUrls.length - 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 backdrop-blur-sm h-12 w-12 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
        >
          <ChevronRight className="h-8 w-8" />
        </Button>
      )}

      {/* Zoom and Tag controls */}
      <div className="absolute top-24 left-6 flex flex-col gap-2 z-10">
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomIn();
          }}
          className="bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/20 text-white h-10 w-10"
          title="Zoom In (+)"
        >
          <span className="text-lg font-bold">+</span>
        </Button>
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomOut();
          }}
          className="bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/20 text-white h-10 w-10"
          title="Zoom Out (-)"
        >
          <span className="text-lg font-bold">−</span>
        </Button>
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleResetZoom();
          }}
          className="bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/20 text-white h-10 w-10"
          title="Reset Zoom (0)"
        >
          <span className="text-xs font-medium">1:1</span>
        </Button>
        <div className="h-px bg-white/20 my-1" />
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onDownload(selectedPhoto);
          }}
          className="bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/20 text-white h-10 w-10"
          title="Download"
        >
          <Download className="h-4 w-4" />
        </Button>

        {/* Photo tagging controls */}
        <div className="h-px bg-white/20 my-1" />
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onTagPhoto(selectedPhoto.id, 'Main Photo');
          }}
          className={`backdrop-blur-md border border-white/20 text-white h-10 w-10 ${
            selectedPhoto.tags?.includes('Main Photo')
              ? 'bg-primary/90 hover:bg-primary'
              : 'bg-black/50 hover:bg-primary/90'
          }`}
          title={selectedPhoto.tags?.includes('Main Photo') ? "Remove Main Photo tag" : "Tag as Main Photo"}
        >
          <Star className="h-4 w-4" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onTagPhoto(selectedPhoto.id, 'Nameplate Photo');
          }}
          className={`backdrop-blur-md border border-white/20 text-white h-10 w-10 ${
            selectedPhoto.tags?.includes('Nameplate Photo')
              ? 'bg-primary/90 hover:bg-primary'
              : 'bg-black/50 hover:bg-primary/90'
          }`}
          title={selectedPhoto.tags?.includes('Nameplate Photo') ? "Remove Nameplate tag" : "Tag as Nameplate Photo"}
        >
          <span className="text-xs font-bold">NP</span>
        </Button>
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onTagPhoto(selectedPhoto.id, 'QR Photo');
          }}
          className={`backdrop-blur-md border border-white/20 text-white h-10 w-10 ${
            selectedPhoto.tags?.includes('QR Photo')
              ? 'bg-primary/90 hover:bg-primary'
              : 'bg-black/50 hover:bg-primary/90'
          }`}
          title={selectedPhoto.tags?.includes('QR Photo') ? "Remove QR tag" : "Tag as QR Code Photo"}
        >
          <span className="text-xs font-bold">QR</span>
        </Button>
      </div>

      {/* Image container */}
      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden px-24 py-20"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
      >
        <img
          src={selectedPhoto.signedUrl}
          alt={selectedPhoto.document_name}
          className="max-w-full max-h-full object-contain transition-transform duration-200 animate-scale-in shadow-2xl"
          style={{
            transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
          }}
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      </div>

      {/* Footer with controls info and zoom level */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent backdrop-blur-sm px-6 py-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6 text-white/80 text-sm">
            <span><kbd className="px-2 py-1 bg-white/10 rounded text-xs">←→</kbd> Navigate</span>
            <span><kbd className="px-2 py-1 bg-white/10 rounded text-xs">Scroll</kbd> Zoom</span>
            <span><kbd className="px-2 py-1 bg-white/10 rounded text-xs">Drag</kbd> Pan</span>
            <span><kbd className="px-2 py-1 bg-white/10 rounded text-xs">ESC</kbd> Close</span>
          </div>
          <div className="bg-black/50 backdrop-blur-md px-4 py-1.5 rounded-lg border border-white/20">
            <span className="text-white text-sm font-medium">{Math.round(zoomLevel * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
